import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  Button,
  List,
  Typography,
  Avatar,
  Image,
  message as antMessage,
} from "antd";
import { CameraFilled, CloseCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import FilePicker from "../common/FilePicker";
import { v4 as uuidv4 } from "uuid";
import { uploadFile } from "../../supabase/storage";

const { Text } = Typography;

const ChatWindow = ({ activeChat, messages, sendMessage }) => {
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [image, setImage] = useState(null);

  const handleSend = async () => {
    if (selectedFile) {
      let photoUrl = null;
      let photouuid = uuidv4();
      try {
        const path = `${activeChat._id}/${photouuid}.jpg`;
        photoUrl = await uploadFile(selectedFile, path);
      } catch (uploadError) {
        antMessage.error("Failed to upload image. Please try again.");
        console.error("Image upload failed", uploadError);
        return;
      }
      sendMessage(
        activeChat._id,
        photoUrl,
        activeChat.otherParticipant._id,
        true,
      );
      setSelectedFile(null);
    } else {
      sendMessage(
        activeChat._id,
        message,
        activeChat.otherParticipant._id,
        false,
      );
      setMessage("");
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const shouldShowDate = (prevMsg, currMsg) => {
    if (!prevMsg) return true;
    return !dayjs(prevMsg.createdAt).isSame(currMsg.createdAt, "day");
  };

  const onFileSelect = (file) => {
    setSelectedFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImage(previewUrl);
  };

  const handleRemovePicture = () => {
    setSelectedFile(null);
    setImage(null);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#f8f9fa",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          zIndex: 1,
        }}
      >
        <Avatar
          src={activeChat.otherParticipant.photoUrl || "/default-avatar.png"}
          size={40}
        />
        <Text strong style={{ marginLeft: "12px", fontSize: "16px" }}>
          {activeChat.otherParticipant.username}
        </Text>
      </div>

      {/* Messages Scroll Area */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        <List
          dataSource={messages}
          renderItem={(msg, index) => {
            const prevMsg = messages[index - 1];
            const isOtherUser = msg.sender === activeChat.otherParticipant._id;
            return (
              <React.Fragment key={msg._id || index}>
                {shouldShowDate(prevMsg, msg) && (
                  <div
                    style={{
                      textAlign: "center",
                      margin: "16px 0 12px 0",
                      fontSize: "11px",
                      color: "#8c8c8c",
                      fontWeight: 500,
                    }}
                  >
                    <span
                      style={{
                        background: "#e8e8e8",
                        padding: "2px 8px",
                        borderRadius: "10px",
                      }}
                    >
                      {dayjs(msg.createdAt).format("DD/MM/YYYY")}
                    </span>
                  </div>
                )}
                <List.Item
                  style={{
                    border: "none",
                    padding: "4px 0",
                    display: "flex",
                    justifyContent: isOtherUser ? "flex-start" : "flex-end",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isOtherUser ? "flex-start" : "flex-end",
                      maxWidth: "75%",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: isOtherUser ? "#ffffff" : "#1677ff",
                        padding: msg.isImage ? "6px" : "10px 14px",
                        borderRadius: isOtherUser
                          ? "14px 14px 14px 2px"
                          : "14px 14px 2px 14px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                        wordBreak: "break-word",
                      }}
                    >
                      {msg.isImage ? (
                        <Image
                          src={msg.content}
                          width={140}
                          height={140}
                          style={{ borderRadius: "8px", objectFit: "cover" }}
                        />
                      ) : (
                        <Text
                          style={{
                            color: isOtherUser ? "#262626" : "#ffffff",
                            fontSize: "14px",
                          }}
                        >
                          {msg.content}
                        </Text>
                      )}
                    </div>
                    <Text
                      style={{
                        fontSize: "10px",
                        color: "#8c8c8c",
                        marginTop: "4px",
                        padding: "0 4px",
                      }}
                    >
                      {dayjs(msg.createdAt).format("HH:mm")}
                    </Text>
                  </div>
                </List.Item>
              </React.Fragment>
            );
          }}
        />
        <div ref={messagesEndRef} />
      </div>

      {/* Input / Attachment Footer */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#ffffff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {!selectedFile ? (
          <Input
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onPressEnter={handleSend}
            style={{ flex: 1, borderRadius: "20px", padding: "8px 16px" }}
          />
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              background: "#fafafa",
              padding: "4px 12px",
              borderRadius: "8px",
              border: "1px border #f0f0f0",
            }}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <Image
                src={image}
                width={48}
                height={48}
                style={{ borderRadius: "6px", objectFit: "cover" }}
              />
              <CloseCircleFilled
                onClick={handleRemovePicture}
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  fontSize: "16px",
                  color: "#ff4d4f",
                  background: "#fff",
                  borderRadius: "50%",
                  cursor: "pointer",
                }}
              />
            </div>
          </div>
        )}

        {!message.trim() && !selectedFile && (
          <FilePicker onFileSelect={onFileSelect}>
            <Button shape="circle" icon={<CameraFilled />} size="large" />
          </FilePicker>
        )}

        {(message.trim() || selectedFile) && (
          <Button
            type="primary"
            onClick={handleSend}
            style={{ borderRadius: "20px", padding: "0 20px" }}
          >
            Send
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatWindow;
